import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../logo2.png';
import SidebarNav from "./SidebarNav";

const ProjectExpense = () => {
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [project, setProject] = useState('');
    const [taxPercentage, setTaxPercentage] = useState('');
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState('');
    const [message, setMessage] = useState('');

    // 新增状态用于费用类别
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [selectedMainCategory, setSelectedMainCategory] = useState('');
    const [selectedSubCategory, setSelectedSubCategory] = useState('');
    const [customExpenseType, setCustomExpenseType] = useState('');

    useEffect(() => {
        fetchBankAccounts();
        fetchExpenseCategories();
    }, []);

    // Fetch the available bank accounts
    const fetchBankAccounts = async () => {
        const { data, error } = await supabase.from('bank_accounts').select('*');
        if (!error) {
            setBankAccounts(data);
        } else {
            console.error('Error fetching bank accounts:', error);
            setMessage('无法获取银行账户信息');
        }
    };

    const fetchExpenseCategories = async () => {

            // 如果费用类别是固定的，可以在这里设置默认值
            setExpenseCategories([
                {
                    main: '运营费用',
                    sub: ['车辆租金', '房租', '公用事业费用', '其他'],
                },
                {
                    main: '项目费用',
                    sub: ['建筑材料', '设备采购', '外包服务', '其他'],
                },
                {
                    main: '行政费用',
                    sub: ['办公用品', '员工福利', '培训费用', '其他'],
                },
            ]);
    };

    const handleProjectExpense = async (e) => {
        e.preventDefault();

        // 输入验证
        if (!project || !amount || taxPercentage === '' || !selectedBankAccount || !selectedMainCategory || !selectedSubCategory) {
            setMessage('请填写所有必填项');
            return;
        }

        // 如果选择的是“其他”，需要输入自定义费用类型
        if (selectedSubCategory === '其他' && customExpenseType.trim() === '') {
            setMessage('请选择“其他”时，请输入自定义费用类型');
            return;
        }

        const expenseAmount = parseFloat(amount);
        const taxRate = parseFloat(taxPercentage);

        if (isNaN(expenseAmount) || expenseAmount <= 0) {
            setMessage('请输入有效的金额');
            return;
        }

        if (isNaN(taxRate) || taxRate < 0) {
            setMessage('请输入有效的税率');
            return;
        }

        // 计算税额和总扣减金额
        const taxAmount = (expenseAmount * taxRate) / 100;
        const totalDeduction = expenseAmount + taxAmount;

        // 获取所选银行账户
        const bankAccount = bankAccounts.find(account => account.id === selectedBankAccount);
        if (!bankAccount) {
            setMessage('请选择有效的银行账户');
            return;
        }

        // 确定最终的费用类型
        const finalExpenseType = selectedSubCategory === '其他' ? customExpenseType.trim() : selectedSubCategory;

        // 显示确认框，确认用户输入的信息
        const confirmation = window.confirm(
            `请确认以下信息:\n\n` +
            `项目类型: ${project}\n` +
            `费用类型: ${finalExpenseType}\n` +
            `描述: ${description}\n` +
            `金额: ${expenseAmount.toFixed(2)}\n` +
            `税率: ${taxRate}%\n` +
            `税额: ${taxAmount.toFixed(2)}\n` +
            `总扣减: ${totalDeduction.toFixed(2)}\n` +
            `银行账户: ${bankAccount.bank_name} - ${bankAccount.account_number}\n` +
            `当前余额: ${bankAccount.balance.toFixed(2)}\n` +
            `扣减后余额: ${(bankAccount.balance - totalDeduction).toFixed(2)}`
        );

        // 如果用户取消确认，不执行后续操作
        if (!confirmation) {
            return;
        }

        // 获取银行账户余额（重新获取以确保最新）
        const { data: bankData, error: bankError } = await supabase
            .from('bank_accounts')
            .select('balance')
            .eq('id', selectedBankAccount)
            .single();

        if (bankError || !bankData) {
            setMessage('获取银行账户余额时出错');
            return;
        }

        const currentBalance = parseFloat(bankData.balance);

        // 检查是否有足够的资金
        if (currentBalance < totalDeduction) {
            setMessage('所选银行账户资金不足');
            return;
        }

        // 计算新的余额
        const newBalance = currentBalance - totalDeduction;

        // 开始事务：插入交易记录并更新银行账户余额
        const { error: transactionError } = await supabase.from('transactions').insert([
            {
                type: '支出',
                amount: expenseAmount,
                tax_percentage: taxRate,
                tax_amount: taxAmount,
                description: `项目支出 - ${project}: ${description}`,
                transaction_type: finalExpenseType, // 新增字段，记录费用类型
                bank_account_id: selectedBankAccount,
                date: new Date(),
            }
        ]);

        if (transactionError) {
            console.error('Transaction Error:', transactionError);
            setMessage('处理支出时出错');
            return;
        }

        // 更新银行账户余额
        const { error: updateError } = await supabase
            .from('bank_accounts')
            .update({ balance: newBalance })
            .eq('id', selectedBankAccount);

        if (updateError) {
            console.error('Update Error:', updateError);
            setMessage('更新银行账户余额时出错');
            return;
        }

        setMessage(`已为项目 "${project}" 添加支出 ${expenseAmount.toFixed(2)} 元，税额 ${taxAmount.toFixed(2)} 元。新的银行账户余额为 ${newBalance.toFixed(2)} 元。`);

        // 重置表单
        setAmount('');
        setTaxPercentage('');
        setDescription('');
        setProject('');
        setSelectedBankAccount('');
        setSelectedMainCategory('');
        setSelectedSubCategory('');
        setCustomExpenseType('');
    };

    // 当主类别变化时，重置子类别和自定义费用类型
    const handleMainCategoryChange = (e) => {
        setSelectedMainCategory(e.target.value);
        setSelectedSubCategory('');
        setCustomExpenseType('');
    };

    // 当子类别变化时，如果选择了“其他”，不做任何处理
    const handleSubCategoryChange = (e) => {
        setSelectedSubCategory(e.target.value);
        if (e.target.value !== '其他') {
            setCustomExpenseType('');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SidebarNav />

            {/* Main Content */}
            <div className="lg:pl-64">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <img
                                src={logo}
                                alt="Kindbridge Investments"
                                className="h-12 w-auto"
                            />
                        </div>
                        <h1 className="mt-6 text-2xl font-semibold text-gray-900">
                            添加项目支出
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            请填写以下表单添加新的项目支出记录
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                        <form onSubmit={handleProjectExpense} className="p-6 space-y-6">
                            {/* Project Type */}
                            <div>
                                <label htmlFor="project" className="block text-sm font-medium text-gray-700">
                                    项目类型<span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    id="project"
                                    type="text"
                                    placeholder="请输入项目类型"
                                    value={project}
                                    onChange={(e) => setProject(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                />
                            </div>

                            {/* Expense Categories */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="expense-category" className="block text-sm font-medium text-gray-700">
                                        费用类别<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <select
                                        id="expense-category"
                                        value={selectedMainCategory}
                                        onChange={handleMainCategoryChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        required
                                    >
                                        <option value="">选择主类别</option>
                                        {expenseCategories.map((category, index) => (
                                            <option key={index} value={category.main}>
                                                {category.main}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedMainCategory && (
                                    <div>
                                        <label htmlFor="sub-category" className="block text-sm font-medium text-gray-700">
                                            子类别<span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <select
                                            id="sub-category"
                                            value={selectedSubCategory}
                                            onChange={handleSubCategoryChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            required
                                        >
                                            <option value="">选择子类别</option>
                                            {expenseCategories
                                                .find(cat => cat.main === selectedMainCategory)
                                                ?.sub.map((sub, idx) => (
                                                    <option key={idx} value={sub}>
                                                        {sub}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* Custom Expense Type */}
                            {selectedSubCategory === '其他' && (
                                <div>
                                    <label htmlFor="custom-expense-type" className="block text-sm font-medium text-gray-700">
                                        自定义费用类型<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <input
                                        id="custom-expense-type"
                                        type="text"
                                        placeholder="请输入自定义费用类型"
                                        value={customExpenseType}
                                        onChange={(e) => setCustomExpenseType(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        required
                                    />
                                </div>
                            )}

                            {/* Description */}
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                    描述<span className="text-red-500 ml-1">*</span>
                                </label>
                                <textarea
                                    id="description"
                                    placeholder="请输入详细描述"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={4}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                />
                            </div>

                            {/* Amount and Tax Rate */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                                        金额<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="amount"
                                            type="number"
                                            placeholder="0.00"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="tax-percentage" className="block text-sm font-medium text-gray-700">
                                        税率<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="tax-percentage"
                                            type="number"
                                            placeholder="0.00, 无税填0"
                                            value={taxPercentage}
                                            onChange={(e) => setTaxPercentage(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bank Account Selection */}
                            <div>
                                <label htmlFor="bank-account" className="block text-sm font-medium text-gray-700">
                                    银行账户<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    id="bank-account"
                                    value={selectedBankAccount}
                                    onChange={(e) => setSelectedBankAccount(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                >
                                    <option value="">请选择银行账户</option>
                                    {bankAccounts.map((account) => (
                                        <option key={account.id} value={account.id}>
                                            {account.bank_name} - {account.account_number} (余额: {account.balance.toFixed(2)})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                    提交支出
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`mt-4 p-4 rounded-md ${
                            message.includes('已为项目')
                                ? 'bg-green-50 text-green-800'
                                : 'bg-red-50 text-red-800'
                        }`}>
                            <p className="text-sm">{message}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectExpense;
